import { type KeyboardEvent, type ReactNode, useState } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { FeatureCard } from '~/components/feature-card';
import { FeatureCardsSection } from '~/components/feature-cards-section';
import { VideoModal } from '~/components/video-modal';
import { trackEvent } from '~/features/analytics/track-event';
import {
  AnalyzeIcon,
  ArrowCircleUpRightIcon,
  ArrowUpIcon,
  BarbellIcon,
  CompassIcon,
  DeRiskIcon,
  DiscIcon,
  EyeIcon,
  ManageIcon,
  MeasureIcon,
  OperationalizeIcon,
  OptimizeIcon,
  OutcomesIcon,
  PblAccelerateIcon,
  PerformanceIcon,
  PlayIcon,
  RowsPlusTopIcon,
  ScaleIcon,
  StrategyIcon,
  TargetIcon,
  UserPlusIcon,
} from '~/features/enterprise/landing-pages/icons';
import { getBenefitCardBorderColor } from '~/features/enterprise/landing-pages/utils/get-benefit-card-border-color';
import { getEnterpriseLandingAnalyticsLabels } from '~/features/enterprise/landing-pages/utils/get-enterprise-landing-analytics-labels';

export type EnterpriseLandingFeatureCardsConfig = {
  /** Section header mark; defaults to `RowsPlusTopIcon` when omitted (e.g. Skills LP). */
  headerIcon?: ReactNode;
  /**
   * `cover` (default): fixed-height slot with `object-fit: cover` (Skills LP).
   * `platform`: responsive width + intrinsic aspect ratio, matching enterprise overview platform feature cards.
   */
  imageLayout?: 'cover' | 'platform';
  blocks: {
    id: string;
    iconId: string;
    themeColor: string;
    bgColor: string;
    image: { src: string; height?: number; width?: number };
    videoId?: string;
  }[];
};

const ICON_SIZE = '18px';

const DEFAULT_HEADER_ICON_PROPS = { color: 'grey.500', height: ICON_SIZE, width: ICON_SIZE } as const;

function getBlockIcon(iconId: string, color: string): ReactNode {
  const props = { color, height: ICON_SIZE, width: ICON_SIZE };
  switch (iconId) {
    case 'accelerate':
      return <PblAccelerateIcon {...props} />;
    case 'analyze':
      return <AnalyzeIcon {...props} />;
    case 'arrow-circle-up-right':
      return <ArrowCircleUpRightIcon {...props} />;
    case 'arrow-up':
      return <ArrowUpIcon {...props} />;
    case 'barbell':
      return <BarbellIcon {...props} />;
    case 'compass':
      return <CompassIcon {...props} />;
    case 'de-risk':
      return <DeRiskIcon {...props} />;
    case 'disc':
      return <DiscIcon {...props} />;
    case 'eye':
      return <EyeIcon {...props} />;
    case 'manage':
      return <ManageIcon {...props} />;
    case 'measure':
      return <MeasureIcon {...props} />;
    case 'monitor':
      return <OutcomesIcon {...props} />;
    case 'operationalize':
      return <OperationalizeIcon {...props} />;
    case 'optimize':
      return <OptimizeIcon {...props} />;
    case 'target':
      return <TargetIcon {...props} />;
    case 'performance':
      return <PerformanceIcon {...props} />;
    case 'scale':
      return <ScaleIcon {...props} />;
    case 'strategy':
      return <StrategyIcon {...props} />;
    case 'user-plus':
      return <UserPlusIcon {...props} />;
    default:
      return null;
  }
}

type BlockImage = EnterpriseLandingFeatureCardsConfig['blocks'][number]['image'];

const PLATFORM_IMAGE_LAYOUT_DEFAULTS = { width: 524, height: 352 } as const;

function BlockImageSlot({
  image,
  imageAlt,
  imageLayout = 'cover',
  videoId,
  onPlay,
}: {
  image: BlockImage;
  imageAlt: string;
  imageLayout?: 'cover' | 'platform';
  videoId?: string;
  onPlay?: () => void;
}) {
  const sharedInteractiveProps =
    videoId != null
      ? {
          'aria-label': 'Play video' as const,
          cursor: 'pointer' as const,
          onClick: onPlay,
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') onPlay?.();
          },
          role: 'button' as const,
          tabIndex: 0 as const,
        }
      : {};

  if (imageLayout === 'platform') {
    const width = image.width ?? PLATFORM_IMAGE_LAYOUT_DEFAULTS.width;
    const height = image.height ?? PLATFORM_IMAGE_LAYOUT_DEFAULTS.height;
    return (
      <Box
        alignSelf={{ base: 'stretch', md: 'center' }}
        borderRadius='8px'
        flex={{ md: '1 1 352px' }}
        overflow='hidden'
        position='relative'
        width='100%'
        {...sharedInteractiveProps}
      >
        <Image
          alt={imageAlt}
          height={height}
          loading='lazy'
          quality={85}
          sizes='(max-width: 768px) 100vw, 522px'
          src={image.src}
          style={{ width: '100%', height: 'auto' }}
          width={width}
        />
        {videoId && (
          <Flex alignItems='center' aria-hidden='true' inset={0} justifyContent='center' position='absolute'>
            <Flex
              alignItems='center'
              bg='blue.500'
              borderRadius='full'
              color='white'
              height='72px'
              justifyContent='center'
              width='72px'
            >
              <PlayIcon boxSize='32px' />
            </Flex>
          </Flex>
        )}
      </Box>
    );
  }

  return (
    <Box
      borderRadius='8px'
      cursor={videoId ? 'pointer' : undefined}
      flex={{ md: '1 1 400px' }}
      height={{ base: '240px', md: '351px' }}
      overflow='hidden'
      position='relative'
      role={videoId ? 'button' : undefined}
      tabIndex={videoId ? 0 : undefined}
      width='100%'
      onClick={videoId ? onPlay : undefined}
      onKeyDown={
        videoId
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onPlay?.();
            }
          : undefined
      }
      {...(videoId ? { 'aria-label': 'Play video' as const } : {})}
    >
      <Image
        alt={imageAlt}
        loading='lazy'
        quality={85}
        sizes='(max-width: 768px) 100vw, 522px'
        src={image.src}
        style={{ objectFit: 'cover' }}
        fill
      />
      {videoId && (
        <Flex alignItems='center' aria-hidden='true' inset={0} justifyContent='center' position='absolute'>
          <Flex
            alignItems='center'
            bg='blue.500'
            borderRadius='full'
            color='white'
            height='72px'
            justifyContent='center'
            width='72px'
          >
            <PlayIcon boxSize='32px' />
          </Flex>
        </Flex>
      )}
    </Box>
  );
}

/** Feature-card grid for enterprise platform landing pages (outcomes, amplify, skills value props, etc.). */
export function EnterpriseLandingFeatureCardsSection({
  config,
  slug,
}: {
  config: EnterpriseLandingFeatureCardsConfig;
  slug: string;
}) {
  const { t } = useTranslation();
  const tPrefix = `enterprise.${slug}.benefits`;
  const analyticsLabels = getEnterpriseLandingAnalyticsLabels(slug);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const imageLayout = config.imageLayout ?? 'cover';

  return (
    <>
      <FeatureCardsSection
        contentGap={10}
        headerIcon={config.headerIcon ?? <RowsPlusTopIcon {...DEFAULT_HEADER_ICON_PROPS} />}
        headerSubtitle={t(`${tPrefix}.subtitle`)}
        sectionPadding={{ base: '56px 0', md: '108px 0' }}
        headerDescription={
          <Text color='grey.500' margin='0 auto' maxWidth='946px'>
            {t(`${tPrefix}.description`)}
          </Text>
        }
        headerTitle={
          <Heading
            as='h2'
            color='grey.900'
            fontSize={{ base: '2.25rem', md: '3.25rem' }}
            fontWeight='600'
            letterSpacing='-2px'
            lineHeight='1.2'
            whiteSpace='pre-wrap'
          >
            {t(`${tPrefix}.title`)}
          </Heading>
        }
      >
        <Flex direction='column' gap={{ base: '32px', md: '58px' }}>
          {config.blocks.map((block, index) => (
            <FeatureCard
              key={block.id}
              description={t(`${tPrefix}.blocks.${block.id}.description`)}
              icon={getBlockIcon(block.iconId, block.themeColor)}
              isReversed={index % 2 !== 0}
              subtitle={t(`${tPrefix}.blocks.${block.id}.label`)}
              themeColor={block.themeColor}
              title={t(`${tPrefix}.blocks.${block.id}.title`)}
              containerProps={{
                bg: block.bgColor,
                borderColor: getBenefitCardBorderColor(block.themeColor),
                borderRadius: '12px',
                borderStyle: 'solid',
                borderWidth: '1px',
                gap: { base: '24px', md: '80px' },
                padding: { base: '24px', md: '32px' },
              }}
              contentProps={{
                flex: { md: '0 2 388px' },
                justifyContent: 'center',
                padding: { base: 0, md: 0 },
                pt: 4,
              }}
              descriptionProps={{
                fontSize: '0.9375rem',
                fontWeight: '500',
                lineHeight: '1.6',
              }}
              imageSlot={
                <BlockImageSlot
                  image={block.image}
                  imageAlt={t(`${tPrefix}.blocks.${block.id}.imageAlt`)}
                  imageLayout={imageLayout}
                  videoId={block.videoId}
                  onPlay={() => {
                    trackEvent({
                      name: 'Enterprise Platform CTA Clicked',
                      type: 'click',
                      label: analyticsLabels.benefitsVideo,
                      value: block.id,
                    });
                    setActiveVideoId(block.videoId!);
                  }}
                />
              }
              titleProps={{
                fontSize: '2.25rem',
                letterSpacing: '-2px',
                lineHeight: '1.2',
              }}
            />
          ))}
        </Flex>
      </FeatureCardsSection>
      <VideoModal
        title={t(`${tPrefix}.videoModalTitle`) as string}
        videoId={activeVideoId}
        onClose={() => setActiveVideoId(null)}
      />
    </>
  );
}
